  import {
    JSX,
    useEffect,
    useRef,
    useState,
  } from "react";
  import {
    View,
    Text,
    ViewStyle,
    TextStyle,
    ScrollView,
  } from "react-native";
  import dayjs from "dayjs";
  import { Router, useRouter } from "expo-router";
  import { useGetCurrentUserQuery, useGetReportHistoryQuery } from "@/_store/currentUser/currentUser.slice";
  import { useGetInsurancePolicyQuery } from "@/_store/insurancePolicy/insurancePolicy.slice";
  import { IResult } from "@/_store/currentUser/contracts";
  import bgWomen from "@/assets/icons/main/bgWomen.png"
  import RedAlertIcon from "@/assets/icons/main/redAlert.svg"
  import PolicyIcon from "@/assets/icons/main/policyIcon.svg"
  import CheckBlock from "@/app/components/CheckBlock";
  import InfoCard from "@/app/components/InfoCard/InfoCard";
  import SubscriptionMonitoring from "@/app/components/SubscriptionMonitoring/SubscriptionMonitoring";
  import CopyToClipboard from "@/app/components/CopyToClipboard/CopyToClipboard";
  import StatsBlock, { IStatItem } from "@/app/components/StatsBlock";
  import CustomButton from "@/app/components/CustomButton";
  import Policy from "@/app/(main)/general/Policy";
  import Container from "@/app/components/Container";
  import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView } from "@gorhom/bottom-sheet";
  import RecentActivityBlock from "@/app/components/RecentActivityBlock";
  import IndividualCheck from "@/app/(main)/general/individualCheck/IndividualCheck";
  import { SUBSCRIPTION_WARNING_DAYS } from "@/_utils/contstans";
  import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";
  import SuccessCompanyCheck from "@/app/(main)/successCompanyCheck/SuccessCompanyCheck";
  import CompanyCheck from "@/app/(main)/companyCheck/CompanyCheck";
  import { useUpdateReportMutation } from "@/_store/report/report.slice";
  import { useCreateReportMutation } from "@/_store/company/company.slice";
  import { styles } from "@/app/components/SheetContentRenderer";
  import BlockSearchBanner from "@/app/components/BlockSearchBanner";
  import { useSelector } from "react-redux";
  import type { RootState } from "@/_store/store";
  import { formatChecksLeft, formatDaysLeft, getChecksWord } from "@/_utils/deadlineItems";

  const INDIVIDUAL_CHECK: number = 1;
  const POLICY_INFO: number = 2;
  const COMPANY_CHECK: number = 3;

  const CUSTOM_BUTTON_CONTAINER: ViewStyle = {
    backgroundColor: "transparent",
    padding: 0,
    alignSelf: "flex-start",
    marginTop: 8,
  };

  const CUSTOM_BUTTON_TEXT: TextStyle = {
    color: "#F5222D",
  };

  const GeneralPage = (): JSX.Element => {
    const [activeSheet, setActiveSheet] = useState<number | null>(null);

    const router: Router = useRouter();
    const bottomSheetRef = useRef<BottomSheet>(null);
    const reportCompanyId: string | null = useSelector((state: RootState) => state?.reportId?.reportId);

    const { data: subscribeData, isLoading: isSubscribeLoading } = useGetCurrentUserQuery(undefined, { refetchOnMountOrArgChange: true });
    const { data: policyData, isLoading: isPolicyLoading } = useGetInsurancePolicyQuery(undefined, { refetchOnMountOrArgChange: true });
    const { data: reportHistory } = useGetReportHistoryQuery({ page: 1, size: 4 }, { refetchOnMountOrArgChange: true });
    const results: IResult[] = reportHistory?.results ?? [];

    const subscribeDaysLeft: number | undefined = subscribeData?.subscription?.days_the_end;
    const totalReports: number | undefined = subscribeData?.subscription?.total_reports;
    const isSubscriptionAlmostOver: boolean = subscribeDaysLeft != null && subscribeDaysLeft <= SUBSCRIPTION_WARNING_DAYS;

    const [createReport, { isLoading, data: createData }] = useCreateReportMutation();
    const [updateReport, { isLoading: isRefreshReport, data: refreshData }] = useUpdateReportMutation();

    const insets: EdgeInsets = useSafeAreaInsets();
    const [reportId, setReportId] = useState<string | null>(null);

    useEffect(() => {
      const newReportId = refreshData?.id ?? createData?.id ?? null;
      setReportId(newReportId);
    }, [createData, refreshData]);

    useEffect(() => {
      if (activeSheet === null) setReportId(null);
    }, [activeSheet]);

    useEffect(() => {
      if (reportCompanyId) {
        setReportId(reportCompanyId);
        openSheet(COMPANY_CHECK);
      }
    }, [reportCompanyId])


    const checkBlocks = [
      {
        index: INDIVIDUAL_CHECK,
        title: "Проверить физ.лицо",
        description: "Финансовые и юридические\nпоказатели физ.лица.",
        bgColor: "#6326BE",
        bgImage: bgWomen,
        buttonColor: "#722ED1",
        checkBalance:  totalReports !== undefined
          ? `${totalReports} ${getChecksWord(totalReports)}`
          : "Информация отсутствует",
        hasInput: false,
      }
    ];

    const openSheet = (index?: number): void => {
      if (index) {
        setActiveSheet(index);
      }
    };

    const handleCompanyInnSearch = async (inn: string): Promise<void> => {
      openSheet(COMPANY_CHECK);
      const result = await createReport({ inn }).unwrap();
      setReportId(result.id);
    };

    const openSubscriptionPage = (): void => router.push("/profile?id=subscription");

    const contentPolicy: JSX.Element = (
      <View className="flex-row items-center gap-4 mt-5 px-5 pb-5">
        <PolicyIcon />
        <View className="flex-col gap-0.5">
          <Text className="text-gray text-sm">Полис СОГАЗ</Text>
          <View className="flex-row gap-1.5">
            <Text className="text-black">{policyData?.number}</Text>
            <CopyToClipboard text={policyData?.number || ""} />
          </View>
        </View>
      </View>
    );

    const statsInfoItems: IStatItem[] = [
      {
        info: `${ subscribeDaysLeft !== undefined ? formatDaysLeft(subscribeDaysLeft) : ""} до окончания подписки`,
        value: subscribeDaysLeft ?? "Информация отсутствует"
      },
      {
        info: "Проверок компаний и ИП осталось",
        value: "Безлимит"
      },
      {
        info: totalReports !== undefined ? formatChecksLeft(totalReports) : "Информация отсутствует",
        value: totalReports ?? "Информация отсутствует"
      },
    ];

    const contentSubscribe: JSX.Element = (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        className="flex-col mt-5 mb-5 pl-3">
        <StatsBlock stats={statsInfoItems} isLoading={isSubscribeLoading} />
      </ScrollView>
    );

    const subscriptionWarning: JSX.Element = (
      <View className="border-red-100 bg-red border rounded-2xl mt-5 mx-5 px-4 py-3 flex-row gap-3">
        <View>
          <RedAlertIcon />
        </View>
        <View className="flex-1">
          <Text className="text-base text-black">
            {subscribeDaysLeft === 0 ? "Срок вашей подписки истёк. На вашу почту был направлен счет для оплаты нового периода" : "Ваша подписка скоро истечет. Пожалуйста, оплатите счет для продления условий."}
          </Text>
          <CustomButton
            style={CUSTOM_BUTTON_CONTAINER}
            textStyle={CUSTOM_BUTTON_TEXT}
            onPress={openSubscriptionPage}
            title="Подробнее"
          />
        </View>
      </View>
    );

    const renderSheetContent = (): JSX.Element | null => {
      switch (activeSheet) {
      case INDIVIDUAL_CHECK:
        return <IndividualCheck subscribeData={subscribeData} />;
      case POLICY_INFO:
        return <Policy policyData={policyData} isLoading={isPolicyLoading} />;
      case COMPANY_CHECK:
        return (
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
            {reportId ? (
              <SuccessCompanyCheck
                reportId={reportId}
                updateReport={updateReport}
                isRefreshReport={isRefreshReport}
              />
            ) : (
              <CompanyCheck createReport={createReport} isLoading={isLoading} />
            )}
          </ScrollView>
        )
      default:
        return null;
      }
    };

    return (
      <>
        <Container style={{ height: 100, flexGrow: 1, paddingHorizontal: 16, paddingBottom: insets.bottom + 100 }}>
          <View className="flex-col gap-3">
            <BlockSearchBanner onSearch={handleCompanyInnSearch} />
            {checkBlocks.map((block, index) => (
              <CheckBlock
                key={index}
                {...block}
                openSheet={openSheet}
              />
            ))}
            <SubscriptionMonitoring />
            {
              policyData?.number && (
                <InfoCard
                  title="Полис"
                  onDetailsPress={() => openSheet(POLICY_INFO)}
                >
                  {contentPolicy}
                </InfoCard>
              )
            }
            <InfoCard
              title="Подписка"
              onDetailsPress={openSubscriptionPage}
              subscribeDaysLeft={subscribeDaysLeft}
              isSubscriptionAlmostOver={isSubscriptionAlmostOver}
            >
              {isSubscriptionAlmostOver && subscriptionWarning}
              {contentSubscribe}
            </InfoCard>
          </View>
          {results.length > 0 && (
            <View>
              <Text className="mt-11 text-2xl font-medium mb-5">Последние проверки</Text>
              {results.map(({ id, name, is_fiz, created }) => (
                <RecentActivityBlock
                  key={id}
                  companyName={name}
                  lastReportDate={created ? dayjs(created).format("DD MMMM YYYY · HH:mm") : "Нет информации"}
                  isFiz={is_fiz}
                  reportId={id}
                />
              ))}
            </View>
          )}
        </Container>
        <BottomSheet
          ref={bottomSheetRef}
          index={activeSheet !== null ? 0 : -1}
          snapPoints={["90%"]}
          enablePanDownToClose
          onChange={(index) => {
            if (index === -1) {
              setActiveSheet(null);
            }
          }}
          backdropComponent={(props) => (
            <BottomSheetBackdrop
              {...props}
              disappearsOnIndex={-1}
              appearsOnIndex={0}
              pressBehavior="close"
            />
          )}
        >
          <BottomSheetScrollView>
            {renderSheetContent()}
          </BottomSheetScrollView >
        </BottomSheet>
      </>
    );
  };

  export default GeneralPage;
